import { Paper, Theme, Typography } from "@mui/material";
import Container, { ContainerProps } from "@mui/material/Container";
import { SxProps } from "@mui/material/styles";
import { ReactElement } from "react";
import { Helmet } from "react-helmet-async";
import { FullPageContainerFooter } from "./components/FullPageContainerFooter";
import { FullPageContainerHeader } from "./components/FullPageContainerHeader";

/**
 * Full Page Container
 * A container that takes up the full page, with pre-defined padding and margin,
 * mobile responsiveness, and an optional elements, such as sidebars, footers, panels, etc.
 */
export type FullPageContainerProps = ContainerProps & {
  pageId: string;
  title?: string;
  heading?: string;
  sx?: SxProps<Theme>;
};

export function FullPageContainer({
  children,
  pageId,
  title,
  heading,
  sx,
  ...props
}: FullPageContainerProps): ReactElement {
  return (
    <Paper
      id={pageId}
      square
      elevation={0}
      sx={[
        {
          height: "100vh",
          ml: { md: 0.5 },
          width: (theme) => ({
            xs: "100%",
            md: `calc(100% - ${theme.spacing(0.5)})`,
          }),
          display: "flex",
          flexDirection: "column",
          flex: 1,
          maxWidth: "100vw",
          overflow: "auto",
          transition: (theme) => theme.transitions.create("width"),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Helmet>
        <title>{title || pageId}</title>
      </Helmet>
      <Container
        {...props}
        sx={{
          py: { xs: 4, md: 6 },
          px: { xs: 1, md: 4 },
          transition: (theme) => theme.transitions.create("width"),
        }}
      >
        {heading && (
          <Typography variant="h1" sx={{ mb: 4 }}>
            {heading}
          </Typography>
        )}
        {children}
      </Container>
    </Paper>
  );
}

FullPageContainer.displayName = "FullPageContainer";

FullPageContainer.Header = FullPageContainerHeader;
FullPageContainer.Footer = FullPageContainerFooter;
